import { Skeleton } from "@mui/material";
import Button from "@mui/material/Button";
import {
  BriefcaseBusiness,
  Download,
  Expand,
  ExternalLink,
  Hash,
  Info,
  Link2,
} from "lucide-react";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { getCompanyAccessName } from "../App";
import HtmlRenderer from "../components/HtmlRenderer";
import ImageSlider from "../components/ImageSlider";
import {
  ButtonContainer,
  Category,
  ClearFix,
  ContentContainer,
  FactText,
  FullscreenIconContainer,
  HtmlRendererContainer,
  ImageContainer,
  Keyword,
  KeywordContainer,
  LinkContainer,
  LinkIcon,
  LinkText,
  OverlayImage,
  Section,
  SectionTitle,
  StyledButton,
  StyledIconWrapper,
} from "../components/StyledComponents";
import useFetchConfig from "../fetchFunction/ConfigFetch";
import useFetchSingleElements from "../fetchFunction/DetailsFetch";
import "../styles/StoryDetails.css";

const StoryDetails = ({ styling, companyAccessname, storyId, onClose }) => {
  const resolvedCompanyAccessname = getCompanyAccessName(companyAccessname);

  const {
    element: storyData,
    loading,
    error,
  } = useFetchSingleElements(resolvedCompanyAccessname, "stories", storyId);

  const { configSettings } = useFetchConfig();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    const setOverlayHeight = () => {
      const overlay = document.querySelector(".mediaoverlay");
      const widget = document.querySelector(".widget-content");

      if (overlay && widget) {
        overlay.style.height = `${widget.scrollHeight}px`;
      }
    };

    setOverlayHeight();

    window.addEventListener("resize", setOverlayHeight);

    return () => window.removeEventListener("resize", setOverlayHeight);
  }, []);

  useEffect(() => {}, [storyId]);

  const story =
    Array.isArray(storyData) && storyData.length > 0 ? storyData[0] : storyData;

  const currentImage = story?.imageUrls?.[currentImageIndex];

  useEffect(() => {
    const widgetContent = document.querySelector(
      ".unnpcw-container .widget-content"
    );
    const overlay = document.querySelector(".mediaoverlay");

    if (widgetContent && overlay) {
      overlay.style.height = `${widgetContent.scrollHeight}px`;
      window.scrollTo(0, 0);
    }
  }, [isOverlayOpen]);

  const openOverlay = (imageUrl) => {
    setSelectedImageUrl(imageUrl);
    setImageLoaded(false);
    setIsOverlayOpen(true);
  };

  const closeOverlay = () => {
    setIsOverlayOpen(false);
    setSelectedImageUrl(null);
  };

  if (loading) {
    return (
      <div className="chapter-entry">
        <Skeleton variant="text" width="60%" height={30} />
        <Skeleton
          variant="rectangular"
          width="100%"
          height={200}
          className="skeleton-rect"
        />
        <div className="skeleton-content">
          <Skeleton variant="text" width="100%" height={30} />
          <Skeleton variant="text" width="100%" height={30} />
          <Skeleton variant="text" width="100%" height={30} />
        </div>
        <Button disabled variant="contained" color="primary">
          Zurück
        </Button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chapter-entry">
        <p>Fehler beim Laden der Daten: {error}</p>
      </div>
    );
  }

  if (!story) {
    return (
      <div className="chapter-entry">
        <p>Story nicht gefunden oder ungültige Daten.</p>
      </div>
    );
  }

  return (
    <div className="chapter-entry">
      <ContentContainer>
        <h3>{story.headline || "Keine Überschrift verfügbar"}</h3>
        {story.company && (
          <div
            style={{ display: "flex", alignItems: "center", marginTop: "10px" }}
          >
            {story.company.logo && (
              <img
                src={story.company.logo}
                alt={`${story.company.name} Logo`}
                style={{
                  maxWidth: "50px",
                  maxHeight: "50px",
                  marginRight: "10px",
                }}
              />
            )}
          </div>
        )}
      </ContentContainer>

      {story.imageUrls && story.imageUrls.length > 0 && (
        <ImageContainer>
          <ImageSlider
            images={story.imageUrls.map((image) => ({
              src: image,
            }))}
            onChange={(index) => setCurrentImageIndex(index)}
          />
          {currentImage && (
            <FullscreenIconContainer onClick={() => openOverlay(currentImage)}>
              <Expand size={32} />
            </FullscreenIconContainer>
          )}
        </ImageContainer>
      )}

      <HtmlRendererContainer>
        <HtmlRenderer
          texts={story.text ? [story.text] : []}
          sanitizeHtml={true}
        />
      </HtmlRendererContainer>
      <ClearFix />

      {isOverlayOpen && (
        <div className="mediaoverlay" onClick={closeOverlay}>
          <div
            className="mediadetail-container"
            onClick={(e) => e.stopPropagation()}
          >
            {!imageLoaded && (
              <>
                <Skeleton variant="text" width="100%" height={30} />
                <Skeleton variant="rectangular" width="100%" height={400} />
              </>
            )}
            <OverlayImage
              src={selectedImageUrl}
              alt="Selected Media"
              style={{ display: imageLoaded ? "block" : "none" }}
              onLoad={() => setImageLoaded(true)}
            />
            {imageLoaded && (
              <div className="unnpcw-container details">
                {currentImage?.description && (
                  <FactText styling={configSettings?.theme || {}}>
                    <StyledIconWrapper styling={styling}>
                      <Info size={16} />
                    </StyledIconWrapper>
                    <strong>Beschreibung:</strong> {currentImage.description}
                  </FactText>
                )}
                {story.publisher && (
                  <FactText styling={configSettings?.theme || {}}>
                    <StyledIconWrapper styling={styling}>
                      <Info size={16} />
                    </StyledIconWrapper>
                    <strong>Herausgeber:</strong> {story.publisher.name}
                  </FactText>
                )}
                {currentImage?.dataname && (
                  <FactText styling={configSettings?.theme || {}}>
                    <StyledIconWrapper styling={styling}>
                      <Info size={16} />
                    </StyledIconWrapper>
                    <strong>Dateiname:</strong> {currentImage.dataname}
                  </FactText>
                )}
              </div>
            )}

            <ButtonContainer>
              <StyledButton
                className="small-button"
                styling={configSettings?.theme || {}}
                variant="contained"
                color="secondary"
                onClick={closeOverlay}
              >
                Zurück
              </StyledButton>
              <a
                href={selectedImageUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: "none" }}
              >
                <StyledButton
                  className="small-button"
                  styling={configSettings?.theme || {}}
                  variant="contained"
                  color="secondary"
                  endIcon={<Download size={16} />}
                >
                  Download
                </StyledButton>
              </a>
            </ButtonContainer>
          </div>
        </div>
      )}

      {story.infolinks?.length > 0 && (
        <Section>
          <SectionTitle styling={styling}>
            <StyledIconWrapper styling={styling}>
              <Link2 size={16} />
            </StyledIconWrapper>
            Infolinks
          </SectionTitle>
          {story.infolinks.map((linkObj, index) => (
            <LinkContainer key={index}>
              <LinkIcon icon={<ExternalLink size={16} />} styling={styling} />
              <LinkText
                href={linkObj.url}
                target="_blank"
                rel="noopener noreferrer"
                styling={styling}
              >
                {linkObj.text || linkObj.url}
              </LinkText>
            </LinkContainer>
          ))}
        </Section>
      )}

      {(story.categories?.length > 0 || story.storyType) && (
        <Section>
          <SectionTitle styling={styling}>
            <StyledIconWrapper styling={styling}>
              <BriefcaseBusiness size={16} />
            </StyledIconWrapper>
            Kategorien
          </SectionTitle>
          <KeywordContainer>
            {story.categories?.map((category, index) => (
              <Category key={index} styling={styling}>
                {category.name}
              </Category>
            ))}
            {story.storyType && (
              <Category styling={styling}>
                {typeof story.storyType === "object"
                  ? story.storyType.name
                  : story.storyType}
              </Category>
            )}
          </KeywordContainer>
        </Section>
      )}

      {story.keywords?.length > 0 && (
        <Section>
          <SectionTitle styling={styling}>
            <StyledIconWrapper styling={styling}>
              <Hash size={16} />
            </StyledIconWrapper>
            Schlagwörter
          </SectionTitle>
          <KeywordContainer>
            {story.keywords.map((keyword, index) => (
              <Keyword key={index} styling={styling}>
                {keyword}
              </Keyword>
            ))}
          </KeywordContainer>
        </Section>
      )}

      <div>
        <StyledButton
          className="small-button"
          styling={configSettings?.theme || {}}
          variant="contained"
          color="primary"
          onClick={onClose}
        >
          Zurück
        </StyledButton>
      </div>
    </div>
  );
};

StoryDetails.propTypes = {
  styling: PropTypes.object.isRequired,
  companyAccessname: PropTypes.string.isRequired,
  storyId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default StoryDetails;
